import type { JSX, Component } from 'solid-js';
import { For, createEffect, createUniqueId } from 'solid-js';
import { SetStoreFunction } from 'solid-js/store';
import { TransactionType, Tax, Amount } from '../globals';
import { Icons, twclsx, Input } from '.';
import MasterSelect from '../shared/MasterSelect';

const defaultTax = {
  rate: 0,
  account_id: '',
  transaction_types: [],
};

export type TaxProps<T> = {
  label: string;
  taxes: Tax[];
  class?: string;
  setFields: SetStoreFunction<T>;
  setErrors?: SetStoreFunction<string[]>;
  entityId: string;
  createNewTax?: () => void;
};

interface MultiSelectRadioGroupProps {
  setTax: (id: string, field: keyof Tax, value: number | string | TransactionType[]) => void;
  tax: Tax;
  class?: string;
}

const MultiSelectRadioGroup: Component<MultiSelectRadioGroupProps> = props => {
  const toggleType = (type: TransactionType) => {
    props.setTax(
      props.tax.randId!,
      'transaction_types',
      props.tax.transaction_types?.includes(type)
        ? props.tax.transaction_types!.filter(t => t !== type)
        : [...props.tax.transaction_types!, type],
    );
  };

  return (
    <>
      <label class={twclsx('block', 'mb-2', 'text-sm', 'font-medium', 'text-gray-900', 'dark:text-white')}>
        Transaction Type
      </label>
      <ul
        class={twclsx(
          'flex-wrap',
          'items-center',
          'w-full',
          'text-sm',
          'font-medium',
          'text-gray-900',
          'bg-bgprimary',
          'flex',
          'gap-2',
          'sm:gap-4',
          'dark:bg-gray-700',
          'dark:text-white',
          'product-type',
          props.class,
        )}
      >
        <For each={Object.keys(TransactionType) as Array<keyof typeof TransactionType>}>
          {type => (
            <li class={twclsx()}>
              <div class={twclsx('flex', 'items-center')}>
                <label class={twclsx('flex', 'font-normal')}>
                  <input
                    class={twclsx(
                      'w-6',
                      'h-6',
                      'mr-2',
                      'text-primary-600',
                      'bg-gray-100',
                      'border-gray-300',
                      'rounded',
                      'focus:ring-primary-500',
                      'dark:focus:ring-primary-600',
                      'dark:ring-offset-gray-700',
                      'dark:focus:ring-offset-gray-700',
                      'focus:ring-2',
                      'dark:bg-gray-600',
                      'dark:border-gray-500',
                    )}
                    type="checkbox"
                    value={TransactionType[type]}
                    checked={props.tax.transaction_types!.includes(TransactionType[type])}
                    onClick={() => toggleType(TransactionType[type])}
                  />
                  <span>{type}</span>
                </label>
              </div>
            </li>
          )}
        </For>
      </ul>
    </>
  );
};

// eslint-disable-next-line comma-spacing
const TaxComponent = <T,>(props: TaxProps<T>): JSX.Element => {
  createEffect(() => {
    if (!props.taxes) {
      props.setFields('taxes' as any, [{ ...defaultTax, randId: createUniqueId() }] as any);
      return;
    }

    if (props.taxes.length === 0 || props.taxes.every(t => t.account_id || t.rate || t.transaction_types?.length)) {
      /* eslint-disable @typescript-eslint/no-explicit-any */
      props.setFields(
        'taxes' as any,
        (props.taxes ? props.taxes.length : 0) as any,
        {
          ...defaultTax,
          randId: createUniqueId(),
        } as any,
      );
      /* eslint-enable @typescript-eslint/no-explicit-any */
    }
  });

  const handleRemoveData = (id: string) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    props.setFields('taxes' as any, ((taxes: Tax[]) => taxes.filter((tax: Tax) => tax.randId !== id)) as any);
  };

  const setTaxes = (id: string, field: keyof Tax, value: number | string | TransactionType[]) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    props.setFields('taxes' as any, ((tax: Tax) => tax.randId === id) as any, field as any, value as any);
  };

  return (
    <div>
      <h3 class={twclsx('font-medium', 'mb-3', 'flex', 'align-middle dark:text-white')}>
        Tax info
        <Icons.Help
          class={twclsx('w-5', 'ms-1')}
          label={
            'This is the tax information related to the product or account, this will be used to auto populate the tax information in the transaction.'
          }
        />
      </h3>
      <div class={twclsx(props.class, 'border', 'border-gray-200', 'rounded-lg', 'p-3', 'mb-3')}>
        <For each={props.taxes}>
          {(tax: Tax) => (
            <div
              class={twclsx(
                props.class,
                'grid',
                'sm:grid-cols-8',
                'gap-3',
                'mb-3',
                'border-b sm:border-0 pb-2 sm:pb-0',
              )}
            >
              <div class={twclsx('col-span-12 sm:col-span-2')}>
                <MasterSelect
                  name="parent"
                  id="parent"
                  label="Account"
                  entityId={props.entityId}
                  class={twclsx('col-span-4')}
                  format={acc => acc.name}
                  placeholder="Account"
                  types={['accounts']}
                  initialValue={tax.account_id as string}
                  onChange={t => {
                    if (typeof t === 'string' || (t && tax.account_id === t.id) || !t) {
                      return;
                    }

                    setTaxes(tax.randId!, 'account_id', t.id);
                  }}
                  createNew={props.createNewTax}
                />

                <Input
                  type="text"
                  class={twclsx('')}
                  inputClass={twclsx('text-right')}
                  id="price"
                  name="rate"
                  placeholder="Rate"
                  label="Rate"
                  value={tax.rate}
                  onChange={e => {
                    const value = e.currentTarget.value || '0';
                    if (Amount.safeParse(value).success) {
                      setTaxes(tax.randId!, 'rate', Number.parseFloat(value));
                    }
                  }}
                  validators={[Tax.shape.rate]}
                />
              </div>
              <div class={twclsx(props.class, 'col-span-12 sm:col-span-5', 'sm:pl-4')}>
                <MultiSelectRadioGroup class={twclsx('')} tax={tax} setTax={setTaxes} />
              </div>
              <div class={twclsx(props.class, 'col-span-12 sm:col-span-1')}>
                <button
                  class={twclsx('sm:px-4', 'text-red-600', 'flex', 'items-center')}
                  onClick={(event: Event) => {
                    event.preventDefault();
                    handleRemoveData(tax.randId!);
                  }}
                >
                  <Icons.Remove class={twclsx('w-4', 'h-4')} />
                  <span class={twclsx('sm:hidden', 'ms-2')}>Remove</span>
                </button>
              </div>
            </div>
          )}
        </For>
      </div>
    </div>
  );
};

export default TaxComponent;
